import React from "react";
import { createBLoC, useInitBLoC, IBLoC, BLoCCallback, BLoCBase } from "@rsm";
import { ResultsModuleTypes, ResultsModuleTypes as T } from "./ResultsModule.types";
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  first,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
} from "rxjs";
import {
  odataAct,
  odataDeleteOne,
  odataFirst,
  odataFuncList,
  odataFuncOne,
  odataList,
  odataPatchOne,
  odataPostOne,
} from "services/odataApi";
import { NavigateFunction, useParams } from "react-router-dom";
import { getToggleble } from "rsm/getToggleable";
import { SearchQueryManager, useQueryManager } from "hooks/useQueryManager";
import { useMainNavigate } from "hooks/useMainNavigate";
import { ResultsModuleHelpers as H } from "./ResultsModule.helpers";
import { toast, toastSuccess } from "components/ToastProvider/ToastProvider";
import { ApiSchema } from "@api-types";
import { confirmDialog } from "primereact/confirmdialog";
import { ResultsModuleRequests } from "./ResultsModule.requests";
import { Globals } from "types/globals";
import { getProjectLink } from "components/ProjectLink/ProjectLink";
import { MatIcon } from "components/MatIcon/MatIcon";
import { FiltersHelper } from "helpers/filtersHelper";
import { FiltersWrapper } from "types/filters-wrapper";
import { debounceAfterFirstEmission } from "helpers/rxjsHelper";
import { ChartDownloadButtonActions } from "components/ChartDownloadButton/ChartDownloadButton";

class BLoC extends BLoCBase<T.State> implements IBLoC {
  /* Props from state */
  public splitBy$ = this.getState$("splitBy").pipe(distinctUntilChanged());
  public selectedMetricQuestionsIds$ = this.getState$("selectedMetricQuestionsIds").pipe(
    distinctUntilChanged(),
  );
  public maxSelectedMetrics$ = this.getState$("maxSelectedMetrics").pipe(distinctUntilChanged());
  public exportSidepanelVisible$ = this.getState$("exportSidepanelVisible").pipe(distinctUntilChanged());
  public metricVisualization$ = this.getState$("metricVisualization").pipe(distinctUntilChanged());
  public benchmarkType$ = this.getState$("benchmarkType").pipe(distinctUntilChanged());
  public benchmarkTypeSelection$ = this.getState$("benchmarkTypeSelection");
  public selectedPanelType$ = this.manager.getString$("selectedPanelType") as Observable<
    ApiSchema.PanelType | undefined
  >;
  public segmentId$ = this.manager.getNumber$("segmentId");
  public selectedQuestionFilterKey$ = this.getState$("selectedQuestionFilterKey");
  public selectedProjectGroupSegmentIds$ = this.manager.getObject$("selectedProjectGroupSegmentIds", []);
  public selectedTestOptionIds$ = this.manager
    .getObject$("selectedTestOptionIds", [])
    .pipe(map((a) => (a?.length ? a : null)));
  public selectedQuestionOptionFilterIds$ = this.manager.getObject$("selectedQuestionOptionFilterIds", {});
  public selectedFilterIds$ = this.manager.getObject$("selectedFilterIds", {});
  public segments$ = this.getState$("segments");
  public schedulingState$ = this.getState$("schedulingState");
  public schedulingPanelOption$ = this.getState$("schedulingPanelOption");
  public selectedSurveyRunIds$ = this.manager.getObject$("selectedSurveyRunIds", undefined);
  public testInfoSidepanelVisible$ = this.getState$("testInfoSidepanelVisible");
  public benchmarkSidepanelVisible$ = this.getState$("benchmarkSidepanelVisible");
  public followersSidePanelVisible$ = this.getState$("followersSidePanelVisible");
  public amaChatSidepanelVisible$ = this.getState$("amaChatSidepanelVisible");

  public segmentSidepanel$ = this.getState$("segmentSidepanel");
  public schedulingPanelVisible$ = this.getState$("schedulingPanelVisible");
  public schedulingPanelEditing$ = this.getState$("schedulingPanelEditing");
  public schedulingEditPanelVisible$ = this.getState$("schedulingEditPanelVisible");
  public selectedDates$ = this.manager.getObject$("selectedDates", undefined).pipe(
    map((value): [Date | null, Date | null] | undefined => {
      if (!value) return undefined;
      const [d1, d2] = value;
      return [d1 !== null ? new Date(d1) : null, d2 !== null ? new Date(d2) : null];
    }),
  );

  public projectCost$ = this.shared$(
    odataFirst("getCreditLogs", {}, { orderBy: "projectId" })
      .applyFilter("projectId", "eq", this.projectId)
      .applyGroupBy(["projectId"], {
        by: "creditAmount",
        with: "sum",
        as: "creditAmount",
      })
      .$(),
  );

  public setExportSidepanelVisible = (visible: boolean = true) =>
    this.setState("exportSidepanelVisible", visible);
  public setSelectedMetricQuestionsIds = (questionsIds: number[] | null) => {
    const maxSelectedMetrics = this.currentState("maxSelectedMetrics") ?? 0;
    let limitedQuestionsIds = questionsIds;

    // If maxSelectedMetrics is set and greater than 0, limit the selection
    if (maxSelectedMetrics > 0 && questionsIds?.length) {
      limitedQuestionsIds = questionsIds.slice(0, maxSelectedMetrics);
    }

    !limitedQuestionsIds?.length && this.setState("splitBy", null);
    this.setState("selectedMetricQuestionsIds", limitedQuestionsIds);
  };
  public setMaxSelectedMetrics = (maxSelectedMetrics: number) => {
    this.setState("maxSelectedMetrics", maxSelectedMetrics);
  };
  /* Modals */
  public modals = getToggleble<{
    testOptionPreview: { file: Globals.ExistingFile; description?: string | null };
    filterSideBar: {};
    scheduleEditorSideBar: {};
    addSegmentModal: { filters: ApiSchema.SegmentResponsesFilters; id?: number | null };
    testInfoModal: {};
  }>();
  public openTestInfoSidepanel = () => this.setState("testInfoSidepanelVisible", true);
  public closeTestInfoSidepanel = () => this.setState("testInfoSidepanelVisible", false);
  public openBenchmarkSidepanel = () => this.setState("benchmarkSidepanelVisible", true);
  public closeBenchmarkSidepanel = () => this.setState("benchmarkSidepanelVisible", false);
  public openFollowersSidePanel = () => this.setState("followersSidePanelVisible", true);
  public closeFollowersSidePanel = () => this.setState("followersSidePanelVisible", false);
  public openAmaChatSidepanel = () => this.setState("amaChatSidepanelVisible", true);
  public closeAmaChatSidepanel = () => this.setState("amaChatSidepanelVisible", false);
  public closeSegmentSidepanel = () => this.setState("segmentSidepanel", undefined);
  public openSchedulingPanel = () => this.setState("schedulingPanelVisible", true);
  public closeSchedulingPanel = () => this.setState("schedulingPanelVisible", false);
  public openSchedulingEditPanel = () => this.setState("schedulingEditPanelVisible", true);
  public closeSchedulingEditPanel = () => this.setState("schedulingEditPanelVisible", false);
  public getModal$ = this.modals.getState$;

  /* Fetched state */
  public project$ = this.getState$("project").pipe(filter((a) => !!a));
  public activeSummaryType$ = this.getState$("activeSummaryType").pipe(distinctUntilChanged());

  public projectGroupSegments$ = this.shared$(
    this.project$.pipe(
      switchMap((p) =>
        p?.projectGroupId ? ResultsModuleRequests.getProjectGroupSegments$(p.projectGroupId) : of(null),
      ),
    ),
  );

  public setActiveSummaryType = (activeSummary: ApiSchema.AiSummaryTypes | null) => {
    this.setState("activeSummaryType", activeSummary);
  };

  private readonly fetchProject = () => {
    this.addSub(
      ResultsModuleRequests.getProject$(this.projectId).subscribe((p) => {
        if (!p) {
          toast({
            severity: "error",
            icon: <MatIcon className="mt-2">error_outline</MatIcon>,
            detail: "Project not found",
          });
          this.navigate("/");
          return;
        }
        this.setState("project", p);
        this.fetchSchedulingState();
      }),
    );
  };
  public projectsRegions$ = this.project$.pipe(
    filter((p) => !!p),
    map((p) => {
      const schedulesFiltered = p?.projectSchedules?.filter((r) => r.surveyProcessingId);
      const schedules = schedulesFiltered?.length ? schedulesFiltered : p?.projectSchedules;
      return schedules?.reduce(
        (p, r) => {
          p[r.regionId || 0] = r.region?.regionName || "n/a";
          return p;
        },
        {} as { [key in number]: string },
      );
    }),
  );

  public selectedRegionId$ = this.shared$(
    this.manager
      .getNumber$("selectedRegionId")
      .pipe(filter((a): a is number => !!a))
      .pipe(distinctUntilChanged()),
  );

  public averageStandardQuestionData$ = this.shared$(
    combineLatest([this.selectedRegionId$, this.project$]).pipe(
      debounceAfterFirstEmission(H.DebounceTimeMs),
      filter(([_, project]) => !!project),
      switchMap(([regionId, project]) =>
        ResultsModuleRequests.getStandardQuestionData$(project.id, [regionId]).pipe(
          map((v) => {
            const reduced = v.reduce(
              (prev, c) => {
                if (!prev[c.standardQuestionId ?? c.questionId]?.length) {
                  prev[c.standardQuestionId ?? c.questionId] = [];
                }
                prev[c.standardQuestionId ?? c.questionId]!.push(c);
                return prev;
              },
              {} as { [key in number]: ResultsModuleRequests.StandardQuestionData[] },
            );
            return Object.keys(reduced)
              .filter((c) => c !== null)
              .map(Number)
              .map((c) => {
                const scores = reduced[c];
                const affirmativeScores = scores?.filter((c) => c.affirmative);
                const topScore = scores
                  ?.filter((c) => c.affirmative)
                  ?.sort((a, b) => (a.answerValue ?? 0) - (b.answerValue ?? 0))?.[0];
                const percentageTop1 = topScore?.percentage ?? 0 / 100;
                const percentageTop2 =
                  (affirmativeScores?.reduce((prev, c) => prev + (c.percentage ?? 0), 0) ?? 0) / 100;
                return {
                  regionId: regionId,
                  benchmarkTypeId: c,
                  benchmarkName: "Test average",
                  projectCount: 1,
                  isAvailable: true,
                  benchmarkType: "Test average",
                  projectType: "",
                  regionCode: "",
                  standardQuestionId: c,
                  top1Percentage: percentageTop1,
                  top1PercentageTotal: percentageTop1,
                  top1Weighted: topScore?.weight ?? 0,
                  top2Percentage: percentageTop2,
                  top2PercentageTotal: percentageTop2,
                  top2Weighted: affirmativeScores?.reduce((prev, c) => prev + (c.weight ?? 0), 0) ?? 0,
                  totalWeight: scores?.reduce((prev, c) => prev + (c.weight ?? 0), 0) ?? 0,
                } as ApiSchema.BenchmarkResult;
              });
          }),
        ),
      ),
    ),
  );

  public benchmarks$ = this.shared$(
    this.selectedRegionId$.pipe(
      switchMap((regionId) =>
        odataFuncList("funcProjectsBenchmarks", {
          key: this.projectId,
          regionIds: [regionId],
        }).$(),
      ),
      map((c) => [
        ...c.value,
        { top2Percentage: 50, benchmarkType: "None" } as ApiSchema.BenchmarkResult & { id: number | null },
      ]),
      tap((bs) => {
        const benchmarkTypeSelectionStr = localStorage.getItem("benchmarkTypeSelection");
        const benchmarkTypeSelection = benchmarkTypeSelectionStr
          ? JSON.parse(benchmarkTypeSelectionStr)
          : null;
        if (
          benchmarkTypeSelection &&
          benchmarkTypeSelection?.type === "benchmark" &&
          bs.find((b) => b.benchmarkTypeId === benchmarkTypeSelection.id && b.isAvailable)
        ) {
          this.setSelectedBenchmarkType(benchmarkTypeSelection);
        } else if (!bs.some((s) => s.benchmarkType !== "None")) {
          this.setSelectedBenchmarkType({ type: "testAverage" });
        }
      }),
    ),
  );
  public selectedBenchmarks$ = this.shared$(
    combineLatest([this.benchmarks$, this.benchmarkTypeSelection$]).pipe(
      switchMap(([b, btype]) =>
        btype.type === "testAverage"
          ? this.averageStandardQuestionData$
          : btype.type === "testOption" && btype.id
            ? this.getAverageStandardQuestionDataByTestOption$(btype.id)
            : of(b?.filter((bb) => bb.benchmarkTypeId === btype.id)),
      ),
    ),
  );

  public withObjective$ = this.manager.getBoolean$("withObjective", false);
  public setWithObjective = (withObjective: boolean) =>
    this.manager.setSearchQuery({
      withObjective: withObjective ? "true" : null,
    });

  public teriatryBarVisible$ = combineLatest([
    this.selectedFilterIds$,
    this.selectedQuestionOptionFilterIds$,
    this.selectedPanelType$,
  ]).pipe(map(([a, b, c]) => !!Object.keys(a ?? {})?.length || !!Object.keys(b ?? {})?.length || !!c));

  public targets$ = this.shared$(
    H.getTargetsReqBuilder(this.projectId)
      .$()
      .pipe(
        map((p) =>
          p.value
            .filter((t) => t.targetMode !== false)
            .reduce(
              (p, c) => {
                const questionLabel =
                  c.question?.questionTitle || c.systemQuestionOption?.systemQuestion?.questionLabel || "";
                if (!p[questionLabel]) {
                  p[questionLabel] = [];
                }
                if (c.systemQuestionOption || c.questionOption) {
                  (p[questionLabel] as string[]).push(
                    c.systemQuestionOption?.answerLabel || c.questionOption?.optionText || "",
                  );
                } else {
                  p[questionLabel] = c.question?.id ?? 0;
                }
                return p;
              },
              {} as { [key in string]: string[] | number },
            ),
        ),
      ),
  );

  private schedules$ = this.shared$(
    this.project$.pipe(
      switchMap(() =>
        H.getProjectScheduleReqBuilder(this.projectId)
          .$()
          .pipe(map((f) => f.value)),
      ),
    ),
  );

  public futureSchedules$ = this.shared$(
    this.schedules$.pipe(
      map((schedules) =>
        schedules
          .filter((s) => s.surveyRunId === null)
          .reduce(
            (p, c) => {
              const index = p.findIndex((pp) => pp.scheduleDate === c.scheduleDate);
              if (index >= 0) {
                p[index]?.id.push(c.id);
              } else {
                p.push({ ...c, id: [c.id] });
              }
              return p;
            },
            [] as (Omit<ApiSchema.ProjectSchedule, "id"> & { id: number[] })[],
          ),
      ),
    ),
  );

  public selectedDatesSurveyIds$ = this.shared$(
    combineLatest([this.schedules$, this.selectedDates$]).pipe(
      map(([schedules, selectedDates]) => {
        if (!schedules || !selectedDates) return undefined;
        const [start, end] = selectedDates;
        return schedules
          .filter((s) => {
            const date = new Date(s.scheduleDate);
            return (!start || date >= start) && (!end || date <= end);
          })
          .map((s) => s.surveyRunId)
          .filter((id): id is number => typeof id === "number");
      }),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    ),
  );

  public weeks$ = this.shared$(
    this.project$.pipe(
      map((p) =>
        p?.projectWeeks
          ?.map((sr) => ({
            date: sr.scheduleDate,
            id: sr.surveyRunId!,
            week: sr.week ?? 0,
          }))
          .reduce((p, c) => {
            const index = p.findIndex((pp) => pp.week === c.week);
            if (index < 0) {
              p.push({ date: new Date(c.date || ""), ids: [c.id], week: c.week });
            } else {
              p[index]!.ids.push(c.id);
            }
            return p;
          }, [] as ResultsModuleTypes.Week[]),
      ),
    ),
  );

  public selectedWeeks$ = this.shared$(
    combineLatest([this.selectedSurveyRunIds$, this.weeks$]).pipe(
      map(([selectedSurveyRunIds, allWeeks]) => {
        if (!selectedSurveyRunIds?.length || !allWeeks) return null;

        return allWeeks.filter((week) => week.ids.every((id: number) => selectedSurveyRunIds.includes(id)));
      }),
      distinctUntilChanged((a, b) => {
        if (!a && !b) return true;
        if (!a || !b) return false;

        const idsA = a.flatMap((w) => w.ids).sort();
        const idsB = b.flatMap((w) => w.ids).sort();

        return idsA.length === idsB.length && idsA.every((id, i) => id === idsB[i]);
      }),
    ),
  );

  public weeksToShow$ = this.shared$(
    combineLatest([this.selectedWeeks$, this.weeks$, this.selectedDates$]).pipe(
      map(([selectedWeeks, allWeeks, selectedDates]) => {
        if (selectedDates) {
          const [start, end] = selectedDates;

          return (
            allWeeks?.filter((week) => {
              const date = new Date(week.date);
              return (!start || date >= start) && (!end || date <= end);
            }) ?? []
          );
        }

        return selectedWeeks ?? allWeeks;
      }),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    ),
  );

  public benchmarkTypes$ = this.shared$(
    combineLatest([this.benchmarks$, this.project$, this.weeks$]).pipe(
      map(([b, project, weeks]) =>
        b
          .filter(
            (bb) =>
              !project ||
              !bb.benchmarkTypeId ||
              project.projectType !== "npd" ||
              bb.benchmarkTypeId < 4 ||
              (bb.benchmarkTypeId === 4 && weeks.find((w) => w.week <= 8)) ||
              (bb.benchmarkTypeId === 5 && weeks.find((w) => w.week > 8 && w.week <= 16)) ||
              (bb.benchmarkTypeId === 6 && weeks.find((w) => w.week > 16 && w.week <= 25)) ||
              (bb.benchmarkTypeId === 7 && weeks.find((w) => w.week > 26)),
          )
          .reduce(
            (p, c) => {
              if (c.benchmarkTypeId) {
                const e = p.find((pp) => pp.id === c.benchmarkTypeId);
                if (e) {
                  e.projectCount = Math.max(e.projectCount, c.projectCount); // Take the highest project count (by standard question)
                  e.isAvailable = e.isAvailable || c.isAvailable; // Allow the benchmark if at least one standard question has it available
                } else {
                  p.push({
                    type: "benchmark",
                    id: c.benchmarkTypeId,
                    label: c.benchmarkName,
                    projectType: project.projectType,
                    regionCode: c.regionCode,
                    projectCount: c.projectCount,
                    isAvailable: c.isAvailable,
                  });
                }
              }
              return p;
            },
            [] as (ResultsModuleTypes.BenchmarkTypeInfo & ResultsModuleTypes.BenchmarkTypeSelection)[],
          ),
      ),
    ),
  );

  public selectedSegment$ = this.shared$(
    combineLatest([
      this.segments$,
      this.segmentId$,
      this.selectedFilterIds$,
      this.selectedQuestionOptionFilterIds$,
      this.selectedPanelType$,
      this.selectedRegionId$,
      this.selectedSurveyRunIds$,
      this.selectedDates$,
    ]).pipe(
      map(
        ([
          segments,
          segmentId,
          filterIds,
          questionOptionIds,
          panelType,
          regionId,
          selectedSurveyRunIds,
          selectedDates,
        ]) => {
          const segment = segments?.find((s) => s.id === segmentId);
          const filter = FiltersHelper.getResponsesFilters(
            filterIds,
            questionOptionIds,
            panelType,
            regionId,
            selectedSurveyRunIds,
            selectedDates,
          );
          return segment
            ? Object.assign({}, segment, {
                matchesCurrentFilters: segments?.find(
                  (s) => s.responsesFilters && FiltersHelper.compareFilters(s.responsesFilters, filter),
                ),
              })
            : null;
        },
      ),
    ),
  );

  public filters$ = this.shared$(
    combineLatest([this.project$, this.selectedRegionId$, this.selectedSurveyRunIds$]).pipe(
      filter(([project]) => !!project),
      distinctUntilChanged(),
      switchMap(([project, selectedRegionId, selectedSurveyRunIds]) =>
        ResultsModuleRequests.getSystemQuestionOptionReqBuilder$(
          selectedRegionId,
          selectedSurveyRunIds,
          project.id,
        ),
      ),
      catchError(() => of(null)),
    ),
  );
  public selectedFilters$ = combineLatest([this.selectedFilterIds$, this.filters$]).pipe(
    map(([ids, filters]) =>
      Object.values(ids).flat()?.length
        ? Object.keys(ids).reduce(
            (p, c) => {
              const f = filters?.filter((f) => ids[c]?.find((id) => id === f.id));
              if (f?.length) {
                p[f[0]?.questionLabel ?? c] = f
                  .filter((a) => !!a.answerLabel)
                  .map((f) => f.answerLabel as never as string)
                  .filter((f) => !!f);
              }
              return p;
            },
            {} as { [key in string]: string[] },
          )
        : null,
    ),
  );
  public filterKeysLabels$ = this.filters$.pipe(
    map((f) =>
      f?.reduce(
        (p, c) =>
          Object.assign(p, {
            [c.questionCode]: c.questionLabel,
          }),
        {} as { [key in string]: string },
      ),
    ),
  );
  public filterKeysTypes$ = this.filters$.pipe(
    map((f) =>
      f?.reduce(
        (p, c) =>
          Object.assign(p, {
            [c.questionCode]: c.systemQuestionTypeId,
          }),
        {} as { [key in string]: Globals.QuestionType },
      ),
    ),
  );
  public filtersByKey$ = this.filters$.pipe(
    map((f) =>
      f?.reduce(
        (r, c) => {
          const key = c.questionCode;
          if (!r[key]) {
            r[key] = [];
          }
          const index = r[key].findIndex((a) => a.answerLabel === c.answerLabel);
          if (index >= 0 && r[key] && r[key][index]) {
            r[key][index] = Object.assign({}, c, { otherIds: [...r[key][index].otherIds, c.id] });
          } else {
            r[key]?.push(Object.assign({}, c, { otherIds: [c.id] }));
          }
          return r;
        },
        {} as {
          [key in string]: (ApiSchema.ProjectSystemQuestionOption & {
            otherIds: number[];
          })[];
        },
      ),
    ),
  );

  public allQuestions$ = this.shared$(
    ResultsModuleRequests.getQuestions$(this.projectId).pipe(
      map(
        (z) =>
          (z
            .map((a) =>
              Object.assign({}, a, {
                question: Object.assign(a.question, {
                  questionOptions: a.question.questionOptions.sort((a, b) => b.id - a.id),
                }),
              }),
            )
            ?.sort((a, b) => {
              const aVal =
                (a.questionPositionId === 7 ? 1 : a.questionPositionId) * 1000000 + (a.sortOrder ?? 0);
              const bVal =
                (b.questionPositionId === 7 ? 1 : b.questionPositionId) * 1000000 + (b.sortOrder ?? 0);
              return aVal - bVal;
            }) as ResultsModuleRequests.ProjectQuestion[]) ?? null,
      ),
      catchError(() => of(null)),
    ),
  );

  public metricQuestions$ = this.shared$(
    this.allQuestions$.pipe(
      map((z) =>
        z?.filter(
          (a) =>
            (a.questionPositionId === Globals.QuestionPositions.CustomMetric &&
              a.question.questionTypeId === Globals.QuestionType.single) ||
            [
              Globals.StandardQuestions.Likeability,
              Globals.StandardQuestions.BrandFit,
              Globals.StandardQuestions.BrandRecognition,
              Globals.StandardQuestions.Desire,
              Globals.StandardQuestions.Distinctiveness,
              Globals.StandardQuestions.PurchaseIntent,
              Globals.StandardQuestions.Experience,
              Globals.StandardQuestions.Consideration,
              Globals.StandardQuestions.Believability,
            ].includes(a.standardQuestionId ?? 0),
        ),
      ),
    ),
  );

  public screenerQuestions$ = this.shared$(
    this.allQuestions$.pipe(
      map((z) => z?.filter((a) => [Globals.QuestionPositions.Screener].includes(a.questionPositionId ?? 0))),
    ),
  );
  public segmentQuestion$ = this.shared$(
    this.allQuestions$.pipe(
      map(
        (z) => z?.filter((a) => [Globals.QuestionPositions.Segment].includes(a.questionPositionId ?? 0))[0],
      ),
    ),
  );

  public audienceQuestions$ = this.shared$(
    this.allQuestions$.pipe(
      map((z) =>
        z?.filter((a) =>
          [
            Globals.QuestionPositions.BeforeLoop,
            Globals.QuestionPositions.Occasions,
            Globals.QuestionPositions.UsageAndAttitudeTracked,
          ].includes(a.questionPositionId ?? 0),
        ),
      ),
    ),
  );

  public insideTheLoopQuestions$ = this.shared$(
    this.allQuestions$.pipe(
      map((z) =>
        z?.filter((a) => [Globals.QuestionPositions.InsideLoop].includes(a.questionPositionId ?? 0)),
      ),
    ),
  );

  public outsideTheLoopQuestions$ = this.shared$(
    this.allQuestions$.pipe(
      map((z) =>
        z?.filter((a) =>
          [Globals.QuestionPositions.OutsideLoop, Globals.QuestionPositions.Custom].includes(
            a.questionPositionId ?? 0,
          ),
        ),
      ),
    ),
  );

  public standardQualQuestions$ = this.shared$(
    this.allQuestions$.pipe(
      map((z) =>
        z?.filter(
          (a) => a.question.questionTypeId !== Globals.QuestionType.text && a.standardQuestionId !== null,
        ),
      ),
    ),
  );
  public standardTextQuestions$ = this.shared$(
    this.allQuestions$.pipe(
      map((z) =>
        z?.filter(
          (a) => a.question.questionTypeId === Globals.QuestionType.text && a.standardQuestionId !== null,
        ),
      ),
    ),
  );
  public followUpQuestions$ = this.shared$(
    this.allQuestions$.pipe(
      map((z) =>
        z?.filter((a) => a.standardQuestionId === 10 || [2, 3, 6, 7, 8].includes(a.questionPositionId ?? 0)),
      ),
    ),
  );
  public trialQuestions$ = this.shared$(
    this.allQuestions$.pipe(map((z) => z?.filter((a) => [13, 14].includes(a.standardQuestionId ?? 0)))),
  );
  public awarenessQuestions$ = this.shared$(
    this.allQuestions$.pipe(map((z) => z?.filter((a) => a.questionPositionId === 4))),
  );
  public occasionsQuestions$ = this.shared$(
    this.allQuestions$.pipe(
      map((z) => z?.filter((a) => a.questionPositionId === Globals.QuestionPositions.Occasions)),
    ),
  );

  public customMetricTextQuestions$ = this.shared$(
    this.allQuestions$.pipe(
      map((z) =>
        z?.filter(
          (a) =>
            a.questionPositionId === Globals.QuestionPositions.CustomMetric &&
            a.question.questionTypeId === Globals.QuestionType.text,
        ),
      ),
    ),
  );

  public filterQuestions$ = this.shared$(
    this.allQuestions$.pipe(
      map((z) =>
        z
          ?.filter((a) => {
            const isText = a.question.questionTypeId === Globals.QuestionType.text;
            const validPosition = [
              Globals.QuestionPositions.Screener,
              Globals.QuestionPositions.InsideLoop,
              Globals.QuestionPositions.OutsideLoop,
              Globals.QuestionPositions.BeforeLoop,
              Globals.QuestionPositions.Awareness,
              Globals.QuestionPositions.Standard,
              Globals.QuestionPositions.Custom,
              Globals.QuestionPositions.Segment,
              Globals.QuestionPositions.Occasions,
              Globals.QuestionPositions.CustomMetric,
            ].includes(a.questionPositionId ?? 0);

            if (!isText && validPosition) {
              if (a.question.standardQuestionId !== null) {
                a.question.questionOptions = a.question.questionOptions?.filter(
                  (option) => option.optionText?.toLowerCase() !== "not sure",
                );
              }
              return true;
            }

            return false;
          })
          ?.sort((a, b) => {
            const tail = [
              Globals.QuestionPositions.InsideLoop,
              Globals.QuestionPositions.OutsideLoop,
              Globals.QuestionPositions.BeforeLoop,
            ];

            const aPos = a.questionPositionId ?? 0;
            const bPos = b.questionPositionId ?? 0;

            if (aPos === Globals.QuestionPositions.Screener && bPos !== aPos) return -1;
            if (bPos === Globals.QuestionPositions.Screener && aPos !== bPos) return 1;

            const aIsTail = tail.includes(aPos);
            const bIsTail = tail.includes(bPos);
            if (aIsTail && !bIsTail) return 1;
            if (!aIsTail && bIsTail) return -1;

            if (aPos === bPos) {
              return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
            }

            return aPos - bPos;
          }),
      ),
      tap((qs) => qs?.length && this.setSelectedQuestionFilterKey(qs[0]!.questionId)),
    ),
  );

  public testOptions$ = this.shared$(ResultsModuleRequests.getTestOptions$(this.projectId));

  public testOptionsPickerList$ = this.testOptions$.pipe(
    map((t) => [
      { id: null, conceptText: "All" },
      ...(t?.map((tt) => ({ id: tt.id, conceptText: tt.conceptText ?? "" })) ?? []),
    ]),
  );

  public selectedTestOptions$ = combineLatest([this.selectedTestOptionIds$, this.testOptions$]).pipe(
    map(([ids, options]) => options?.filter((o) => ids?.includes(o.id))?.map((a) => a)),
  );
  public getAverageStandardQuestionDataByTestOption$ = (testOptionId: number) =>
    this.shared$(
      combineLatest([
        this.selectedRegionId$,
        this.project$,
        this.testOptions$,
        this.selectedFilterIds$,
        this.selectedQuestionOptionFilterIds$,
        this.selectedPanelType$,
        this.selectedProjectGroupSegmentIds$,
      ]).pipe(
        debounceAfterFirstEmission(H.DebounceTimeMs),
        filter(([regionId, project, testOptions]) => !!regionId && !!project && !!testOptions?.length),
        switchMap(
          ([regionId, project, testOptions, filters, questionOptions, panelType, categorySegmentIds]) =>
            ResultsModuleRequests.getStandardQuestionData$(
              project.id,
              [regionId],
              testOptionId,
              filters,
              questionOptions,
              panelType,
              categorySegmentIds,
            ).pipe(
              map((v) => {
                const reduced = v.reduce(
                  (prev, c) => {
                    if (!prev[c.standardQuestionId ?? c.questionId]?.length) {
                      prev[c.standardQuestionId ?? c.questionId] = [];
                    }
                    prev[c.standardQuestionId ?? c.questionId]!.push(c);
                    return prev;
                  },
                  {} as { [key in number]: ResultsModuleRequests.StandardQuestionData[] },
                );
                return Object.keys(reduced)
                  .filter((c) => c !== null)
                  .map(Number)
                  .map((c) => {
                    const scores = reduced[c];
                    const affermativeScores = scores?.filter((c) => c.affirmative);
                    const topScore = scores
                      ?.filter((c) => c.affirmative)
                      ?.sort((a, b) => (a.answerValue ?? 0) - (b.answerValue ?? 0))?.[0];
                    const testOption = testOptions?.find((t) => t.id === topScore?.testOptionId);
                    return {
                      regionId: regionId,
                      benchmarkTypeId: c,
                      benchmarkName: `${testOption?.conceptText ?? "Concept"} average`,
                      projectCount: 1,
                      isAvailable: true,
                      benchmarkType: `${testOption?.conceptText ?? "Concept"} average`,
                      projectType: "",
                      regionCode: "",
                      standardQuestionId: c,
                      testOptionId: topScore?.testOptionId,
                      top1Percentage: (topScore?.percentage ?? 0) / 100,
                      top1PercentageTotal: (topScore?.percentage ?? 0) / 100,
                      top1Weighted: topScore?.weight ?? 0,
                      top2Percentage:
                        (affermativeScores?.reduce((prev, c) => prev + (c.percentage ?? 0), 0) ?? 0) / 100,
                      top2PercentageTotal:
                        (affermativeScores?.reduce((prev, c) => prev + (c.percentage ?? 0), 0) ?? 0) / 100,
                      top2Weighted: affermativeScores?.reduce((prev, c) => prev + (c.weight ?? 0), 0) ?? 0,
                      totalWeight: scores?.reduce((prev, c) => prev + (c.weight ?? 0), 0) ?? 0,
                    };
                  });
              }),
            ),
        ),
      ),
    );

  public selectedQuestionOptionFilter$ = combineLatest([
    this.selectedQuestionOptionFilterIds$,
    this.allQuestions$,
  ]).pipe(
    map(([ids, questions]) =>
      Object.values(ids).flat()?.length
        ? Object.keys(ids).reduce(
            (p, c) => {
              const question = questions?.find((q) => q.questionId === Number(c));
              if (question && Object.keys(ids[Number(c)] ?? {}).length) {
                p[question.question.questionTitle] = question.question.questionOptions
                  .filter((o) => Object.keys(ids[Number(c)] ?? {})?.find((a) => Number(a) === o.id))
                  .map((o) => o.optionText ?? "Unknown");
              }
              return p;
            },
            {} as { [key in string]: string[] },
          )
        : null,
    ),
  );

  public totalSurveyRespondentCount$ = this.shared$(
    this.schedules$.pipe(
      switchMap((schedules) =>
        odataList<{ count: number }>("getResponses", { orderBy: "projectId" })
          .applyGroupBy(["projectId", "regionId"], {
            as: "count",
            by: "surveySessionId",
            with: "countdistinct",
          })
          .applyFilter("projectId", "eq", this.projectId)
          .applyFilterIn(
            "regionId",
            schedules.map((s) => s.regionId || 0),
          )
          .$()
          .pipe(map((r) => r?.value?.reduce((acc, curr) => acc + (curr.count ?? 0), 0) ?? 0)),
      ),
    ),
  );

  public projectResponseCountByTestOption$ = this.shared$(
    combineLatest([
      this.selectedFilterIds$.pipe(distinctUntilChanged()),
      this.selectedDates$.pipe(distinctUntilChanged()),
      this.selectedQuestionOptionFilterIds$.pipe(distinctUntilChanged()),
      this.selectedRegionId$.pipe(distinctUntilChanged()),
      this.selectedSurveyRunIds$.pipe(distinctUntilChanged()),
      this.selectedPanelType$.pipe(distinctUntilChanged()),
      this.weeksToShow$.pipe(distinctUntilChanged()),
      this.selectedProjectGroupSegmentIds$.pipe(distinctUntilChanged()),
    ]).pipe(
      debounceAfterFirstEmission(H.DebounceTimeMs),
      switchMap(
        ([
          filterIds,
          dates,
          questionOptionsIds,
          regionId,
          selectedSurveyRunIds,
          panelType,
          weeksToShow,
          selectedProjectGroupSegmentIds,
        ]) => {
          const includeIds = FiltersHelper.filterSubDictionaryByValue(questionOptionsIds, true)
            .map((a) => a.ids)
            .flat();
          const excludeIds = FiltersHelper.filterSubDictionaryByValue(questionOptionsIds, false)
            .map((a) => a.ids)
            .flat();
          return odataFuncOne("funcProjectsResponsesCount", {
            key: this.projectId,
            filters: {
              questionOptionsIds: includeIds?.length ? includeIds : undefined,
              excludeQuestionOptionsIds: excludeIds?.length ? excludeIds : undefined,
              filterIds: Object.values(filterIds).flat(),
              surveyIds: selectedSurveyRunIds ?? undefined,
              testOptionIds: undefined,
              regionIds: [regionId],
              dateFrom: (dates?.[0] ?? undefined) as never,
              dateTo: (dates?.[1] ?? undefined) as never,
              panelType: panelType,
              projectGroupSegmentIds: selectedProjectGroupSegmentIds?.length
                ? selectedProjectGroupSegmentIds
                : undefined,
            },
          })
            .$()
            .pipe(
              map((responseList) =>
                responseList.reduce(
                  (acc, response) => {
                    const testOptionId = response.testOptionId ?? 0;

                    const testGroup = acc[testOptionId] || {
                      count: 0,
                      weight: 0,
                      stdResponsesCount: 0,
                      stdQuestions: [],
                      byWeek: {},
                    };

                    testGroup.weight = Math.max(testGroup.weight, response.weight);
                    testGroup.count = Math.max(testGroup.count, response.count);
                    testGroup.stdQuestions.push(response);
                    if (response.standardQuestionId) {
                      testGroup.stdResponsesCount = Math.max(testGroup.stdResponsesCount, response.count);
                    }

                    weeksToShow.forEach(({ week }) => {
                      const matches = response.byWeek?.filter((bw) => bw.week === week) ?? [];

                      const weekCount = matches.reduce((max, bw) => Math.max(max, bw.count ?? 0), 0);
                      const weekWeight = matches.reduce((max, bw) => Math.max(max, bw.weight ?? 0), 0);

                      testGroup.byWeek[week] = {
                        count: Math.max(testGroup.byWeek[week]?.count || 0, weekCount),
                        weight: Math.max(testGroup.byWeek[week]?.weight || 0, weekWeight),
                      };
                    });

                    acc[testOptionId] = testGroup;
                    return acc;
                  },
                  {} as Record<
                    number,
                    {
                      count: number;
                      stdResponsesCount: number;
                      weight: number;
                      stdQuestions: ApiSchema.RespondentCount[];
                      byWeek: Record<number, { count: number; weight: number }>;
                    }
                  >,
                ),
              ),
            );
        },
      ),
    ),
  );

  public projectResponseCount$ = combineLatest([this.projectResponseCountByTestOption$, this.project$]).pipe(
    map(([responseCountsByTestOption, project]) => {
      const aggregatedResponse = Object.values(responseCountsByTestOption).reduce(
        (acc, response) => {
          acc.count = project?.splitCellQuota
            ? response.count + acc.count
            : Math.max(acc.count, response.count);
          acc.weight = project?.splitCellQuota
            ? response.weight + acc.weight
            : Math.max(acc.weight, response.weight);
          if (response.stdResponsesCount) {
            acc.stdResponsesCount = project?.splitCellQuota
              ? response.stdResponsesCount + acc.stdResponsesCount
              : Math.max(acc.stdResponsesCount, response.stdResponsesCount);
          }

          acc.stdQuestions.push(...response.stdQuestions);

          Object.entries(response.byWeek).forEach(([week, stats]) => {
            const weekKey = Number(week);
            const prev = acc.byWeek[weekKey] ?? { count: 0, weight: 0 };

            acc.byWeek[weekKey] = {
              count: Math.max(prev.count, stats.count),
              weight: Math.max(prev.weight, stats.weight),
            };
          });

          return acc;
        },
        {
          count: 0,
          stdResponsesCount: 0,
          weight: 0,
          stdQuestions: [] as ApiSchema.RespondentCount[],
          byWeek: {} as Record<number, { count: number; weight: number }>,
        },
      );
      // }
      return aggregatedResponse;
    }),
  );

  public projectTotalResponseCount$ = combineLatest([
    this.project$,
    this.projectResponseCountByTestOption$,
    this.selectedTestOptionIds$,
    this.projectResponseCount$,
  ]).pipe(
    map(([project, countsByTestOption, selectedTestOptionIds, projectResponseCount]) => {
      if (project?.splitCellQuota !== 1) {
        return projectResponseCount?.count ?? 0;
      }

      // If monadic, return the total response count for all test options due to how they are asked
      if (!countsByTestOption) return 0;
      if (
        selectedTestOptionIds === null ||
        selectedTestOptionIds === undefined ||
        selectedTestOptionIds.length === 0
      ) {
        return Object.values(countsByTestOption).reduce((acc, item) => acc + (item?.count ?? 0), 0);
      }
      return selectedTestOptionIds.reduce(
        (acc, testOptionId) => acc + (countsByTestOption[testOptionId]?.count ?? 0),
        0,
      );
    }),
    shareReplay(1),
  );

  public selectedFilterCount$ = combineLatest([
    this.selectedFilterIds$,
    this.selectedQuestionOptionFilterIds$,
    this.selectedSurveyRunIds$,
    this.selectedPanelType$,
    this.selectedDates$,
    this.selectedProjectGroupSegmentIds$,
  ]).pipe(
    map(
      ([filters, screeners, surveyRunIds, panelType, dates, projectGroupSegmentIds]) =>
        Object.values(filters || {}).filter((f) => !!f?.length).length +
        Object.values(screeners || {}).filter((f) => !!Object.keys(f ?? {})?.length).length +
        (surveyRunIds?.length || 0) +
        (panelType ? 1 : 0) +
        (dates?.[0] || dates?.[1] ? 1 : 0) +
        (projectGroupSegmentIds?.length || 0),
    ),
  );

  public panelTypes$ = this.shared$(
    combineLatest([this.selectedRegionId$, this.project$]).pipe(
      map(([selectedRegionId, project]) =>
        project?.projectWeeks
          ?.filter((w) => w.surveySampleProviderId && w.regionId === selectedRegionId)
          ?.map((w) => Globals.getPanelType(Number(w.surveySampleProviderId)))
          ?.filter((v, i, a) => v && a.indexOf(v) === i),
      ),
    ),
  );
  public filtersWrapper: FiltersWrapper = {
    selectedRegionId$: this.selectedRegionId$,
    selectedFilterIds$: this.selectedFilterIds$,
    selectedQuestionOptionFilterIds$: this.selectedQuestionOptionFilterIds$,
    selectedTestOptionIds$: this.selectedTestOptionIds$,
    selectedPanelType$: this.selectedPanelType$,
    selectedProjectGroupSegmentIds$: this.selectedProjectGroupSegmentIds$,
    selectedUandAFilterIds$: of(null),
  };

  /* State set functions */
  public setSelectedRegionId = (selectedRegionId: number) => {
    Number(this.manager.getCurrentValue("selectedRegionId")) !== selectedRegionId &&
      this.manager.setSearchQuery({
        selectedRegionId,
        selectedFilterIds: {},
        selectedQuestionOptionFilterIds: {},
        selectedDates: undefined,
      });
  };
  public setSelectedTestOptionId = (optionIds: number[] | null) =>
    this.manager.setSearchQuery({ selectedTestOptionIds: optionIds });
  public setSelectedSurveyRunIds = (surveyRunIds: number[] | null) =>
    this.manager.setSearchQuery({
      selectedSurveyRunIds: surveyRunIds,
      selectedDates: undefined,
    });
  public setSelectedDates = (selectedDates: [Date | null, Date | null] | undefined) => {
    const adjustedDates = selectedDates && [
      selectedDates[0] ? new Date(selectedDates[0].setHours(0, 0, 0, 0)) : null,
      selectedDates[1] ? new Date(selectedDates[1].setHours(23, 59, 59, 999)) : null,
    ];

    this.manager.setSearchQuery({
      selectedDates: adjustedDates,
      selectedSurveyRunIds: undefined,
    });
  };
  public setSelectedQuestionFilterKey = (key: number | null) =>
    this.setState("selectedQuestionFilterKey", key);

  public benchmarkMode$ = this.getState$("benchmarkMode").pipe(distinctUntilChanged());
  public setBenchmarkMode = (mode: "percentage" | "index") => {
    this.setState("benchmarkMode", mode);
    localStorage.setItem("benchmarkMode", mode);
  };

  public resetAllFilters = () =>
    this.manager.setSearchQuery({
      selectedFilterIds: {},
      selectedQuestionOptionFilterIds: {},
      selectedPanelType: undefined,
      segmentId: null,
      selectedDates: undefined,
      selectedProjectGroupSegmentIds: undefined,
    });
  public resetFilterIds = (filterKey?: string) =>
    filterKey
      ? this.manager
          .getObject$("selectedFilterIds")
          .pipe(first())
          .subscribe((r) => {
            delete r[filterKey];
            this.manager.setSearchQuery({
              selectedFilterIds: r,
            });
          })
      : this.manager.setSearchQuery({
          selectedFilterIds: {},
          selectedDates: undefined,
          selectedQuestionOptionFilterIds: [],
        });
  public resetQuestionOptionIds = (questionId?: number) =>
    questionId
      ? this.manager
          .getObject$("selectedQuestionOptionFilterIds")
          .pipe(first())
          .subscribe((r) => {
            delete r[questionId];
            this.manager.setSearchQuery({
              selectedQuestionOptionFilterIds: r,
            });
          })
      : this.manager.setSearchQuery({
          selectedQuestionOptionFilterIds: {},
        });

  public setSelectedFilterId = (groupLabel: string, ids: number[]) =>
    this.manager.setSearchQuery({
      selectedFilterIds: Object.assign(
        {},
        JSON.parse(this.manager.getCurrentValue("selectedFilterIds") || "{}"),
        {
          [groupLabel]: ids,
        },
      ),
    });

  public setSelectedQuestionOptionFilterIds = (questionId: number, ids: { [key in number]: boolean }) =>
    this.manager.setSearchQuery({
      selectedQuestionOptionFilterIds: Object.assign(
        {},
        JSON.parse(this.manager.getCurrentValue("selectedQuestionOptionFilterIds") || "{}"),
        {
          [questionId]: ids,
        },
      ),
    });
  public setSelectedPanelType = (type: ApiSchema.PanelType) =>
    this.manager.setSearchQuery({
      selectedPanelType: type,
    });
  public resetPanelType = () =>
    this.manager.setSearchQuery({
      selectedPanelType: undefined,
    });

  /* Modal funcs */
  public openFilterSidebar = () => this.modals.setState("filterSideBar", {});
  public openScheduleEditorSideBar = () => this.modals.setState("scheduleEditorSideBar", {});

  public openTestOptionPreview = (file: Globals.ExistingFile, description?: string | null) =>
    this.modals.setState("testOptionPreview", { file, description });
  public openSegmentSidepanel = (segment?: ResultsModuleRequests.Segment) => {
    combineLatest([
      this.selectedFilterIds$.pipe(first()),
      this.selectedQuestionOptionFilterIds$.pipe(first()),
      this.selectedPanelType$.pipe(first()),
      this.selectedRegionId$.pipe(first()),
      this.selectedSurveyRunIds$.pipe(first()),
      this.selectedDates$.pipe(first()),
      this.selectedProjectGroupSegmentIds$.pipe(first()),
    ]).subscribe(
      ([
        filterIds,
        questionOptionFilterIds,
        panelType,
        regionId,
        selectedSurveyRunIds,
        selectedDates,
        projectGroupSegmentIds,
      ]) =>
        this.setState("segmentSidepanel", {
          id: segment?.id,
          filters:
            segment?.responsesFilters ??
            FiltersHelper.getResponsesFilters(
              filterIds,
              questionOptionFilterIds,
              panelType,
              regionId,
              selectedSurveyRunIds,
              selectedDates,
              projectGroupSegmentIds,
            ),
        }),
    );
  };

  public setSchedulingPanelOption = (option: T.State["schedulingPanelOption"]) => {
    this.setState("schedulingPanelOption", option);
  };

  public closeModal = (refetch?: boolean) => {
    if (refetch) {
      this.fetchProject();
      this.fetchSchedulingState();
    }
    this.closeSchedulingPanel();
    this.closeSchedulingEditPanel();
    this.modals.resetState();
  };

  /* Other functions */
  constructor(
    public projectId: number,
    private readonly navigate: NavigateFunction,
    private readonly manager: SearchQueryManager<T.QueryState>,
  ) {
    super({
      metricVisualization: "top2combined",
      benchmarkType: "top2",
      schedulingState: undefined,
      schedulingPanelOption: "upcoming",
      activeSummaryType: null,
      amaChatSidepanelVisible: window.location.pathname.includes("/chat"),
      benchmarkMode: (localStorage.getItem("benchmarkMode") as "percentage" | "index") || "percentage",
      maxSelectedMetrics: 0,
      benchmarkTypeSelection: { type: "benchmark", id: 1 },
    });
  }
  public onInit() {
    this.fetchSegments();
    this.fetchProject();

    // Set default regionId if not set already set in the url
    this.addSub(
      this.projectsRegions$.pipe(first()).subscribe((regions) => {
        const curRegionId = Number(this.manager.getCurrentValue("selectedRegionId"));
        const allowedRegionIds = Object.keys(regions).map(Number);
        (!curRegionId || !allowedRegionIds.includes(curRegionId)) &&
          this.setSelectedRegionId(allowedRegionIds[0] ?? 1);
      }),
    );

    this.addSub(
      combineLatest([
        this.metricQuestions$,
        this.maxSelectedMetrics$,
        this.selectedMetricQuestionsIds$,
      ]).subscribe(([metricQuestions, maxSelectedMetrics, selectedMetricQuestionsIds]) => {
        const max = maxSelectedMetrics ?? 0;
        if (max > 0 && metricQuestions?.length) {
          if (!selectedMetricQuestionsIds?.length && metricQuestions.length > max) {
            const firstMaxMetrics = metricQuestions.slice(0, max).map((q) => q.questionId);
            this.setState("selectedMetricQuestionsIds", firstMaxMetrics);
          } else if (selectedMetricQuestionsIds?.length && selectedMetricQuestionsIds.length > max) {
            const trimmedIds = selectedMetricQuestionsIds.slice(0, max);
            this.setState("selectedMetricQuestionsIds", trimmedIds);
          }
        }
      }),
    );
  }

  public fetchSegments = (setNewSegment?: number) =>
    this.addSub(
      ResultsModuleRequests.getSegments$(this.projectId).subscribe((s) => {
        this.setState("segments", s);
        if (setNewSegment) {
          this.setSegment(setNewSegment);
        }
      }),
    );

  public fetchSchedulingState = () =>
    this.addSub(
      ResultsModuleRequests.getSchedulingState$(this.projectId).subscribe((s) =>
        this.setState("schedulingState", s),
      ),
    );

  public segmentToFEFilters$ = (segmentId: number) =>
    this.segments$.pipe(
      filter((s) => !!s),
      map((s) => s.find((s) => s.id === segmentId)),
      first(),
      switchMap((segment) =>
        segment
          ? combineLatest([this.filters$.pipe(first()), this.allQuestions$.pipe(first())]).pipe(
              map(([filters, questions]) => {
                const selectedFilters = segment.responsesFilters?.filterIds?.length
                  ? filters?.filter((f) => segment?.responsesFilters?.filterIds?.includes(f.id))
                  : null;
                const filterQuestion = questions?.filter((q) =>
                  q.question.questionOptions.find(
                    (o) =>
                      segment.responsesFilters?.questionOptionsIds?.includes(o.id) ||
                      segment.responsesFilters?.excludeQuestionOptionsIds?.includes(o.id),
                  ),
                );
                return {
                  selectedSurveyRunIds: segment.responsesFilters?.surveyIds?.length
                    ? segment.responsesFilters?.surveyIds
                    : undefined,
                  selectedTestOptionIds: segment.responsesFilters?.testOptionIds?.length
                    ? segment.responsesFilters.testOptionIds
                    : null,
                  selectedDates:
                    segment.responsesFilters?.dateFrom || segment.responsesFilters?.dateTo
                      ? [segment.responsesFilters?.dateFrom, segment.responsesFilters?.dateTo]
                      : undefined,
                  selectedPanelType: segment.responsesFilters?.panelType ?? undefined,
                  selectedRegionId: segment.responsesFilters?.regionIds?.length
                    ? segment.responsesFilters.regionIds[0]
                    : undefined,
                  selectedFilterIds:
                    selectedFilters?.reduce(
                      (prev, cur) => {
                        if (!prev[cur.questionCode]) {
                          prev[cur.questionCode] = [];
                        }
                        prev[cur.questionCode]?.push(cur.id);
                        return prev;
                      },
                      {} as {
                        [x: string]: number[];
                      },
                    ) ?? undefined,
                  selectedQuestionOptionFilterIds: filterQuestion?.reduce(
                    (prev, c) => {
                      prev[c.questionId] = c.question.questionOptions
                        .filter(
                          (o) =>
                            segment.responsesFilters?.questionOptionsIds?.includes(o.id) ||
                            segment.responsesFilters?.excludeQuestionOptionsIds?.includes(o.id),
                        )
                        .reduce(
                          (p2, c2) => {
                            p2[c2.id] = !!segment.responsesFilters?.questionOptionsIds?.includes(c2.id);
                            return p2;
                          },
                          {} as {
                            [key in number]: boolean;
                          },
                        );
                      return prev;
                    },
                    {} as {
                      [x: number]: {
                        [x: number]: boolean;
                      };
                    },
                  ),
                  selectedProjectGroupSegmentIds: segment.responsesFilters?.projectGroupSegmentIds?.length
                    ? segment.responsesFilters.projectGroupSegmentIds
                    : undefined,
                } as ResultsModuleTypes.SegmentFeObj;
              }),
            )
          : of(null),
      ),
    );

  public projectLastRunCost$ = this.shared$(
    this.project$.pipe(
      switchMap((p) => {
        if (!p?.projectSchedules) {
          return of(0);
        }

        const schedulesWithCreditLogs = p.projectSchedules.filter(
          (schedule) => schedule.creditLog?.creditAmount !== undefined,
        );

        if (schedulesWithCreditLogs.length === 0) {
          return of(0);
        }

        const schedulesByDate = schedulesWithCreditLogs.reduce(
          (groups, schedule) => {
            const date = schedule.scheduleDate;
            if (!groups[date]) {
              groups[date] = [];
            }
            groups[date].push(schedule);
            return groups;
          },
          {} as Record<string, typeof schedulesWithCreditLogs>,
        );

        const latestDate = Object.keys(schedulesByDate).sort().pop();

        if (!latestDate) {
          return of(0);
        }

        const latestGroupSchedules = schedulesByDate[latestDate];
        if (!latestGroupSchedules) {
          return of(0);
        }

        const totalCost = latestGroupSchedules.reduce((sum, schedule) => {
          return sum + (schedule.creditLog?.creditAmount ?? 0);
        }, 0);

        return of(totalCost * -1);
      }),
    ),
  );

  public setSegment = (segmentId: number) =>
    this.segmentToFEFilters$(segmentId).subscribe(
      (r) =>
        r &&
        this.manager.setSearchQuery({
          ...r,
          segmentId,
          selectedTestOptionIds: this.manager.getCurrentValue("selectedTestOptionIds"),
        }),
    );

  public updateSegment = () => {
    combineLatest([
      this.segmentId$.pipe(first()),
      this.selectedFilterIds$.pipe(first()),
      this.selectedQuestionOptionFilterIds$.pipe(first()),
      this.selectedPanelType$.pipe(first()),
      this.selectedRegionId$.pipe(first()),
      this.selectedSurveyRunIds$.pipe(first()),
      this.selectedDates$.pipe(first()),
    ])
      .pipe(
        switchMap(
          ([
            segmentId,
            filterIds,
            questionOptionFilterIds,
            panelType,
            regionId,
            selectedSurveyRunIds,
            selectedDates,
          ]) =>
            segmentId
              ? odataPatchOne(
                  "patchSegmentsOne",
                  {
                    filters: FiltersHelper.getResponsesFilters(
                      filterIds,
                      questionOptionFilterIds,
                      panelType,
                      regionId,
                      selectedSurveyRunIds,
                      selectedDates,
                    ),
                  },
                  { key: segmentId },
                ).$()
              : of(null),
        ),
      )
      .subscribe((r) => {
        if (r) {
          this.fetchSegments(r.id);
          toastSuccess(
            "Filter set saved",
            "All team members can access this filter set in the filters menu.",
          );
        }
      });
  };

  public deleteSegment = (segmentId: number) =>
    confirmDialog({
      appendTo: document.body,
      acceptClassName: "p-button-danger",
      header: "Delete filter set",
      acceptLabel: "Delete",
      message: `Are you sure you want to delete filter set "${this.currentState("segments")?.find((s) => s.id === segmentId)?.name ?? segmentId}"?`,
      accept: () =>
        odataDeleteOne("deleteSegmentsOne", { key: segmentId })
          .$()
          .subscribe(() => {
            this.fetchSegments();
            this.resetAllFilters();
          }),
    });

  public cloneProject = () =>
    odataAct("actProjectsDuplicate", {}, { key: this.projectId })
      .$()
      .subscribe((r) => {
        toastSuccess("Project cloned", `Project '${r.projectName}' has been cloned.`);
        this.navigate(getProjectLink(r));
      });

  public setSelectedBenchmarkType = (benchmarkTypeSelection: ResultsModuleTypes.BenchmarkTypeSelection) => {
    this.setState("benchmarkTypeSelection", benchmarkTypeSelection);
    benchmarkTypeSelection
      ? localStorage.setItem("benchmarkTypeSelection", JSON.stringify(benchmarkTypeSelection))
      : localStorage.removeItem("benchmarkTypeSelection");
  };

  public setSplitBy = (splitBy: ResultsModuleTypes.SplitBy | null) => {
    this.selectedTestOptionIds$.pipe(first()).subscribe((testOptionIds) => {
      splitBy?.ids?.length &&
        (testOptionIds?.length ?? 0) > 1 &&
        this.setSelectedTestOptionId(testOptionIds!.slice(0, 1));
      this.metricQuestions$.pipe(first()).subscribe((questions) => {
        this.setStates({
          splitBy,
          selectedMetricQuestionsIds: this.currentState("selectedMetricQuestionsIds")?.length
            ? [this.currentState("selectedMetricQuestionsIds")![0]!]
            : questions?.length
              ? [questions[0]!.questionId]
              : null,
        });
      });
    });
  };
  public setMetricVisualization = (metricVisualization: ResultsModuleTypes.MetricVisualization) => {
    if (metricVisualization === "top2combined") this.setState("benchmarkType", "top2");
    this.setStates({ metricVisualization });
  };
  public setBenchmarkType = (benchmarkType: ResultsModuleTypes.BenchmarkType) =>
    this.setStates({ benchmarkType });

  public setSelectedProjectGroupSegmentIds = (projectGroupSegmentIds: number[]) =>
    this.manager.setSearchQuery({
      selectedProjectGroupSegmentIds: projectGroupSegmentIds,
    });

  public toggleSelectedProjectGroupSegmentIds = (projectGroupSegmentId: number) =>
    this.manager.setSearchQuery({
      selectedProjectGroupSegmentIds: this.manager
        .getCurrentValueObject("selectedProjectGroupSegmentIds", [])
        .includes(projectGroupSegmentId)
        ? this.manager
            .getCurrentValueObject("selectedProjectGroupSegmentIds", [])
            .filter((id) => id !== projectGroupSegmentId)
        : [
            ...this.manager.getCurrentValueObject("selectedProjectGroupSegmentIds", []),
            projectGroupSegmentId,
          ],
    });

  public readonly updateProjectObjective = (summary: string) => {
    odataPostOne("postProjectObjectives", {
      projectId: this.projectId,
      summary,
    })
      .$()
      .subscribe((objective) => {
        this.mutateState("project", (prev) =>
          Object.assign({}, prev, {
            activeProjectObjective: { summary: objective.summary },
            activeProjectObjectiveId: objective.id,
          }),
        );
        this.setWithObjective(true);
        toast({
          severity: "success",
          summary: "Objective saved",
          detail:
            "We're currently analysing your objective and preparing summaries. This will take a few minutes.",
        });
        this.fetchProject();
      });
  };

  public deleteProjectObjective = (projectObjectiveId: number) =>
    confirmDialog({
      appendTo: document.body,
      acceptClassName: "p-button-danger",
      header: "Delete objective",
      acceptLabel: "Delete",
      message: "Are you sure you want to delete your custom objective?",
      accept: () => {
        this.setWithObjective(false);
        odataDeleteOne("deleteProjectObjectivesOne", { key: projectObjectiveId })
          .$()
          .subscribe(() => {
            this.mutateState("project", (prev) =>
              Object.assign({}, prev, { activeProjectObjective: null, activeProjectObjectiveId: null }),
            );
            this.fetchProject();
          });
      },
    });
}

const [ContextWrapper, useBloc] = createBLoC<BLoC>();

export const useResultsModuleBLoC = useBloc;

export const ResultsModuleBLoC: React.FC<BLoCCallback<BLoC>> = ({ children }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useMainNavigate();
  const manager = useQueryManager<T.QueryState>();
  const bloc = useInitBLoC(
    () => (projectId ? new BLoC(Number(projectId), navigate, manager) : null),
    [projectId],
  );
  return bloc ? <ContextWrapper key={projectId} bloc={bloc} children={children} /> : null;
};
